import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import LandingPage from './pages/LandingPage';
import SecurityLogin from './pages/SecurityLogin';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Batches from './pages/Batches';
import BatchDetail from './pages/BatchDetail';
import Compliance from './pages/Compliance';
import ChainOfCustody from './pages/ChainOfCustody';
import VerifyBatch from './pages/VerifyBatch';
import NfcResolve from './pages/NfcResolve';
import CompareBatches from './pages/CompareBatches';
import TestResults from './pages/TestResults';
import SensorTestConsole from './pages/SensorTestConsole';
import DashboardLayout from './layouts/DashboardLayout';
import { Toaster } from './components/ui/sonner';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Routing model
 * ----------------------------------------------------------------------
 * Public surface (root domain, no auth gates):
 *   /                  → Public LandingPage  (Warm Ocean Clarity)
 *   /verify/:id        → Consumer QR verification (kept at root)
 *   /nfc/:tagId        → NFC sticker resolver    (kept at root)
 *
 * MVP / Operator portal (under `/mvp/*`):
 *   /mvp/access        → Stage 1: MVP security login (JWT gate)
 *   /mvp/login         → Stage 2: Role selection portal
 *   /mvp               → Protected operator Dashboard (and all sub-routes)
 *
 * Catch-all → /  (public landing)
 *
 * Two-stage auth model:
 *   1. mvpAuth → JWT-based MVP access gate (24h). Must pass first.
 *   2. user    → role-selection state from the role portal.
 */
function App() {
  const [mvpAuth, setMvpAuth] = useState(null);   // null=loading, false=not auth, object=auth
  const [user, setUser] = useState(null);          // role portal user
  const [token, setToken] = useState(null);        // legacy role token

  // Bootstrap: validate stored MVP token and restore role-portal user
  useEffect(() => {
    const bootstrap = async () => {
      const stored = localStorage.getItem('mvp_token');
      const expiresAtStr = localStorage.getItem('mvp_token_expires_at');
      const username = localStorage.getItem('mvp_username');
      const expiresAt = expiresAtStr ? parseInt(expiresAtStr, 10) : 0;

      if (!stored || !expiresAt || Date.now() >= expiresAt) {
        clearMvpStorage();
        setMvpAuth(false);
        return;
      }

      try {
        await axios.get(`${BACKEND_URL}/api/mvp-auth/me`, {
          headers: { Authorization: `Bearer ${stored}` },
        });
        setMvpAuth({ token: stored, username, expiresAt });

        // Restore role portal session if present
        const storedUser = localStorage.getItem('user');
        const storedToken = localStorage.getItem('token');
        if (storedUser && storedToken) {
          setUser(JSON.parse(storedUser));
          setToken(storedToken);
        }
      } catch {
        clearMvpStorage();
        setMvpAuth(false);
      }
    };
    bootstrap();
  }, []);

  const clearMvpStorage = () => {
    localStorage.removeItem('mvp_token');
    localStorage.removeItem('mvp_username');
    localStorage.removeItem('mvp_token_expires_at');
  };

  const clearRoleStorage = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  const handleMvpAuthenticated = (auth) => {
    setMvpAuth(auth);
  };

  const handleRoleLogin = (userData, userToken) => {
    setUser(userData);
    setToken(userToken);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', userToken);
  };

  const handleLogout = async () => {
    try {
      if (mvpAuth?.token) {
        await axios.post(
          `${BACKEND_URL}/api/mvp-auth/logout`,
          {},
          { headers: { Authorization: `Bearer ${mvpAuth.token}` } },
        );
      }
    } catch { /* non-fatal */ }
    clearMvpStorage();
    clearRoleStorage();
    setMvpAuth(false);
    setUser(null);
    setToken(null);
  };

  // Loading splash while we validate stored token
  if (mvpAuth === null) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-50"
        data-testid="auth-bootstrap-loading"
      >
        <div className="text-slate-500 text-sm">Checking session…</div>
      </div>
    );
  }

  // Helper to render a protected MVP page wrapped in the dashboard chrome.
  const mvpPage = (Page) => (
    <Protected mvpAuth={mvpAuth} user={user}>
      <DashboardLayout user={user} onLogout={handleLogout}>
        <Page user={user} />
      </DashboardLayout>
    </Protected>
  );

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* ===== Public surface (root domain) ============================ */}
          <Route path="/" element={<LandingPage lang="en" />} />
          <Route path="/no" element={<LandingPage lang="no" />} />
          {/* QR verification — kept at root so existing printed codes work */}
          <Route path="/verify/:id" element={<VerifyBatch />} />
          {/* NFC sticker resolver — kept at root */}
          <Route path="/nfc/:tagId" element={<NfcResolve />} />

          {/* ===== MVP / Operator portal =================================== */}

          {/* Stage 1 — Security Login */}
          <Route
            path="/mvp/access"
            element={
              !mvpAuth ? (
                <SecurityLogin onAuthenticated={handleMvpAuthenticated} />
              ) : (
                <Navigate to={user ? '/mvp' : '/mvp/login'} replace />
              )
            }
          />

          {/* Stage 2 — Role selection portal */}
          <Route
            path="/mvp/login"
            element={
              !mvpAuth ? (
                <Navigate to="/mvp/access" replace />
              ) : !user ? (
                <Login onLogin={handleRoleLogin} onLogout={handleLogout} />
              ) : (
                <Navigate to="/mvp" replace />
              )
            }
          />

          {/* Protected MVP routes */}
          <Route path="/mvp" element={mvpPage(Dashboard)} />
          <Route path="/mvp/batches" element={mvpPage(Batches)} />
          <Route path="/mvp/batches/:id" element={mvpPage(BatchDetail)} />
          <Route path="/mvp/compliance" element={mvpPage(Compliance)} />
          <Route path="/mvp/chain-of-custody" element={mvpPage(ChainOfCustody)} />
          <Route path="/mvp/compare" element={mvpPage(CompareBatches)} />
          <Route path="/mvp/test-results" element={mvpPage(TestResults)} />
          <Route path="/mvp/sensor-console" element={mvpPage(SensorTestConsole)} />

          {/* Catch-all → public landing */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

/**
 * Route guard for MVP routes:
 *  - No MVP auth → /mvp/access (Security Login)
 *  - MVP auth but no role chosen → /mvp/login (Role Portal)
 *  - Both present → render children
 */
const Protected = ({ mvpAuth, user, children }) => {
  if (!mvpAuth) return <Navigate to="/mvp/access" replace />;
  if (!user) return <Navigate to="/mvp/login" replace />;
  return children;
};

export default App;
